// Rich content configuration specifically for AI Assistants
export type AIAssistantRichContentTagType =
  | "INFO_BOX"
  | "HIGHLIGHT"
  | "ACTION_ITEM"
  | "WARNING"
  | "CODE_BLOCK"
  | "QUOTE"
  | "CALLOUT"
  | "TASK"
  | "CHECKLIST"
  | "COLOR_PREVIEW"
  | "TABLE"
  | "UNORDERED_LIST"
  | "ORDERED_LIST";

export type AIAssistantRichContentTag = {
  regex: RegExp;
  component: string;
  className: string;
  description?: string;
};

export type ExtractedAIContent = {
  type: AIAssistantRichContentTagType;
  content: string;
  rawMatch: string;
};

// Configuration for AI Assistant rich content tags
export const AI_ASSISTANT_RICH_CONTENT_TAGS: Record<
  AIAssistantRichContentTagType,
  AIAssistantRichContentTag
> = {
  INFO_BOX: {
    // Support both custom tags and markdown admonition :::info fences
    regex:
      /(?:<info-box>([\s\S]*?)<\/info-box>)|(?:^:::\s*info\s*\n([\s\S]*?)\n:::\s*$)/gm,
    component: "AIInfoBox",
    className: "bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg",
    description: "Important information highlighted in a blue box",
  },
  HIGHLIGHT: {
    regex: /<highlight>(.*?)<\/highlight>/g,
    component: "AIHighlight",
    className: "bg-yellow-100 px-2 py-1 rounded font-medium text-yellow-800",
    description: "Highlighted text with yellow background",
  },
  ACTION_ITEM: {
    regex: /<action-item>([\s\S]*?)<\/action-item>/g,
    component: "AIActionItem",
    className:
      "bg-green-50 border border-green-200 p-6 rounded-lg font-medium text-green-800",
    description: "Call-to-action items in green styling",
  },
  WARNING: {
    // Support custom tag and :::warning
    regex:
      /(?:<warning>([\s\S]*?)<\/warning>)|(?:^:::\s*warning\s*\n([\s\S]*?)\n:::\s*$)/gm,
    component: "AIWarning",
    className:
      "bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg text-red-700",
    description: "Warning messages in red styling",
  },
  CODE_BLOCK: {
    regex: /<code-block>([\s\S]*?)<\/code-block>/g,
    component: "AICodeBlock",
    className:
      "bg-gray-100 border rounded-lg p-6 font-mono text-sm overflow-x-auto",
    description: "Code blocks with monospace font and gray background",
  },
  QUOTE: {
    regex: /<quote>([\s\S]*?)<\/quote>/g,
    component: "AIQuote",
    className: "border-l-4 border-gray-300 pl-4 italic text-gray-600",
    description: "Quoted text with left border and italic style",
  },
  CALLOUT: {
    // Support custom tag and :::callout
    regex:
      /(?:<callout>([\s\S]*?)<\/callout>)|(?:^:::\s*callout\s*\n([\s\S]*?)\n:::\s*$)/gm,
    component: "AICallout",
    className:
      "bg-purple-50 border border-purple-200 p-6 rounded-lg text-purple-800",
    description: "Special callout sections in purple styling",
  },
  TASK: {
    regex: /<task>([\s\S]*?)<\/task>/g,
    component: "AITask",
    className:
      "flex items-start gap-3 p-6 bg-slate-50 border border-slate-200 rounded-lg",
    description: "Task items with checkbox and content",
  },
  CHECKLIST: {
    regex: /<checklist>([\s\S]*?)<\/checklist>/g,
    component: "AIChecklistGroup",
    className: "space-y-2 mb-4 ml-4 text-sm",
    description: "Checklist items with checkboxes",
  },
  COLOR_PREVIEW: {
    regex: /(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})/g,
    component: "AIColorPreview",
    className: "inline-flex items-center gap-2",
    description: "Color codes with preview boxes",
  },
  TABLE: {
    // Match standard multi-line markdown tables (allow leading/trailing spaces)
    // and a single-line form like "| h1 | h2 | | r1c1 | r1c2 | | r2c1 | r2c2 |"
    regex: /(?:^\s*\|.+\|\s*$\n?)+|(?:\|[^\n]+\|\s*(?:\|\s*\|[^\n]+\|)+)/gm,
    component: "AITable",
    className: "overflow-x-auto my-4",
    description: "Markdown-style tables",
  },
  UNORDERED_LIST: {
    // Use dotall to match multi-line list content
    regex: /<ul>([\s\S]*?)<\/ul>/g,
    component: "AIUnorderedList",
    className: "list-disc list-inside space-y-2 mb-4 ml-4 text-sm",
    description: "Bullet point lists",
  },
  ORDERED_LIST: {
    // Use dotall to match multi-line list content
    regex: /<ol>([\s\S]*?)<\/ol>/g,
    component: "AIOrderedList",
    className: "list-decimal list-inside space-y-2 mb-4 ml-4 text-sm",
    description: "Numbered lists",
  },
};
